import _ from 'lodash'
import React, { useContext, useEffect, useRef, useState } from 'react'
import { Container, Slide } from '@mui/material'

import { AB_TESTING_NAMES, GAMES_PAGE_REDESIGN_GROUP } from '@playback-rewards/shared-libraries'

import { getUserPlatform, sendGA4Event } from '../../utils/utils'
import { ANALYTICS_EVENT, EXPERIMENT_SURFACE, USER_PLATFORM } from '../../constants'
import AuthContext from '../../context/AuthContext'
import useExperiment from '../../hooks/useExperiment'
import GamesRedesign from './components/redesign'

const observerOptions = {
  root: null,
  rootMargin: '0px 0px -70px 0px', //The footer is overlapping the offer card slider, subtracting the navigation bar height
  threshold: 0.1, // Trigger the callback when 10% of the GameCard is in view
}

const GamesListContent = ({ isLoading, offers, previousPage }) => {
  const { user } = useContext(AuthContext)

  const variant = useExperiment(AB_TESTING_NAMES.GAMES_PAGE_REDESIGN, EXPERIMENT_SURFACE.GAMES_LIST)

  // Users without a group (not expected) get variant A and report no exposure.
  const renderedVariant = variant ?? GAMES_PAGE_REDESIGN_GROUP.VARIANT_A
  const experiment = variant
    ? { experiment_id: AB_TESTING_NAMES.GAMES_PAGE_REDESIGN, variant }
    : undefined

  // Read by the long-lived observer, so it never sees a stale user or experiment.
  const latest = useRef({ user, experiment })
  latest.current = { user, experiment }

  const trackGameCardInView = (entries, observer) => {
    const { user, experiment } = latest.current
    entries.forEach((entry) => {
      if (entry.isIntersecting) {
        const offer = JSON.parse(entry.target.closest('.gamesListCard')?.dataset?.offer)

        if (offer) {
          const offerIndex = offer.index + 1 //Start count from 1
          sendGA4Event(ANALYTICS_EVENT.OFFER_TILE_VIEW, {
            offer_id: offer.id,
            is_activated: !!offer.activatedOffer,
            is_ua_offer: offer.isUAOffer,
            featured_offer_slot: offer.isFeatured ? offerIndex : null,
            category: offer.category || null,
            category_row_number: _.get(offer, 'categoryRowNumber', null),
            slot_within_category: offer.isFeatured ? null : offerIndex,
            offer_tile_view_guid: offer.offerTileViewGUID,
            offer_campaign_id:
              getUserPlatform(user.platform) === USER_PLATFORM.ANDROID
                ? offer.campaignIdAndroid
                : offer.campaignIdIOS,
            ...experiment,
          })
        }
        observer.unobserve(entry.target)
      }
    })
  }

  // One observer per page: a new one per render reported duplicate impressions.
  const [observer, setObserver] = useState(null)
  useEffect(() => {
    const pageObserver = new IntersectionObserver(trackGameCardInView, observerOptions)
    setObserver(pageObserver)

    return () => pageObserver.disconnect()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const games = (
    <div>
      <Container maxWidth="sm" sx={styles.gamesContainer}>
        <GamesRedesign
          offers={offers}
          observer={observer}
          variant={renderedVariant}
          experiment={experiment}
        />
      </Container>
    </div>
  )

  return (
    <>
      {previousPage ? (
        games
      ) : (
        <Slide direction="up" in={!isLoading && !previousPage} mountOnEnter unmountOnExit>
          {games}
        </Slide>
      )}
    </>
  )
}

const styles = {
  gamesContainer: {
    padding: 0,
  },
}

export default GamesListContent
